import React, { useEffect, useRef, useState } from "react";

import { InputNumber as AntInputNumber } from "antd";

import { parseInputRules } from "utils";

import { Flex, Form, InputLabel, InputNumberProps, Option, Select } from "components";

type InputNumberWithUnitSelectionProps = Omit<InputNumberProps, "suffix" | "onChange"> & {
  onChange?: (value: number | null, unit: string | null) => void;
  units?: string[];
  unitLabel?: string;
  unitValue?: string | null;
  unitDisplayOverride?: Record<string, string>;
};

const COMMON_UNIT_DISPLAY_LOOKUP: Record<string, string> = {
  tonnes: "t",
  kilograms: "kg",
  cubic_meters: "m³",
  liters: "l",
  mj_nel: "MJ NEL",
  mj_me: "MJ ME",
};

export const InputNumberWithUnitSelector = ({
  className,
  labelClassName = "",
  decimalSeparator = ",",
  defaultValue,
  disabled,
  initialValue,
  unitValue,
  precision,
  label,
  tooltip,
  message,
  min,
  max,
  name,
  rules,
  validateFirst,
  step,
  value,
  dataTestId,
  units,
  unitLabel = "Unit",
  unitDisplayOverride,
  onBlur = () => {},
  onChange = (_, __) => {},
}: InputNumberWithUnitSelectionProps) => {
  const inputElement = useRef<HTMLInputElement>(null);
  const [hasBeenFocused, setHasBeenFocused] = useState(false);
  const [internalValue, setInternalValue] = useState<number | null>(initialValue ?? null);
  const [internalUnit, setInternalUnit] = useState<string | null>(unitValue ?? null);

  const displayOverride = { ...COMMON_UNIT_DISPLAY_LOOKUP, ...unitDisplayOverride };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setHasBeenFocused(false);
    onBlur(e);
  };

  const handleChangeNumber = (newValue: number | null) => {
    setInternalValue(newValue);
    onChange(newValue, internalUnit);
  };

  const handleChangeUnit = (newUnit: string) => {
    setInternalUnit(newUnit);
    onChange(internalValue || null, newUnit);
  };

  const handleLabelClick = () => {
    setHasBeenFocused(true);
    inputElement.current?.focus();
  };

  const active = !!(hasBeenFocused || defaultValue || initialValue || initialValue === 0 || internalValue || internalValue === 0);
  const required = rules && rules[0] && rules[0].required;

  useEffect(() => {
    if (inputElement.current) {
      setInternalValue(inputElement.current.valueAsNumber);
    }
  }, [inputElement]);

  useEffect(() => {
    setInternalUnit(unitValue ?? null);
  }, [unitValue]);

  return (
    <Form.Item
      className={`floating-input ${className} ${hasBeenFocused ? "active" : ""} ${disabled ? "disabled" : "message"}`}
      colon={false}
      label={<InputLabel active={active} required={required} label={label} className={labelClassName} handleClick={handleLabelClick} tooltip={tooltip} />}
      name={name}
      rules={parseInputRules(rules, message)}
      validateFirst={validateFirst ?? false}
      initialValue={initialValue || initialValue === 0 ? initialValue : defaultValue}
      normalize={(value) => {
        if (typeof value === "string" && value.trim() !== "") {
          const normalizedValue = value.replace(",", ".");
          const num = Number(normalizedValue);
          return Number.isNaN(num) ? value : num;
        }
        return value;
      }}
    >
      <Flex className={`input-inline-container ${hasBeenFocused ? "active" : ""}`}>
        <AntInputNumber
          className="number-input-with-unit-selection"
          data-testid={dataTestId}
          decimalSeparator={decimalSeparator || ","}
          disabled={disabled}
          min={min}
          max={max}
          precision={precision}
          ref={inputElement}
          step={step}
          value={value}
          onBlur={handleBlur}
          onChange={handleChangeNumber}
          onFocus={() => setHasBeenFocused(true)}
        />
        {units ? (
          <div className="unit-selector">
            {
              // @ts-expect-error Need to convert
              <Select
                label={unitLabel}
                dataTestId="unit-selector"
                name={`${name}-unit`}
                initialValue={internalUnit}
                onChange={handleChangeUnit}
                value={internalUnit}
                dropdownStyle={{ zIndex: 10010 }}
                optionFilterProp="children"
              >
                {units.map((unit, index) => (
                  <Option key={index} value={unit}>
                    {displayOverride[unit] ?? unit}
                  </Option>
                ))}
              </Select>
            }
          </div>
        ) : null}
      </Flex>
    </Form.Item>
  );
};
